/**
 * Tiny in-memory store for the share-link `uuid` granted to a recipient.
 *
 * The Super Picker public SPA is reachable in two modes:
 *
 *   1. **Agent EXTERNAL mode** — a logged-in agent with `Role='EXTERNAL'`
 *      reaches the public app from the agent shell. They have a normal
 *      session cookie and `signedFetch` works as-is.
 *
 *   2. **Share-link recipient mode** — an unauthenticated visitor lands on
 *      `/super-picker-public?uuid=<UUID>` after an agent shared the link.
 *      They have no session cookie; the backend authorizes their requests
 *      only when `?uuid=` is supplied (see `requireShareAccess` in
 *      `backend/src/routes/superPickerShare.ts`).
 *
 * `SuperPickerPublicApp` calls `setPublicShareUuid` on mount when it
 * detects a uuid in the URL; `publicFetch` consumes it on every request.
 */

let activeShareUuid: string | null = null;

/** Returns the current share uuid, or `null` when running in agent mode. */
export function getPublicShareUuid(): string | null {
  return activeShareUuid;
}

/**
 * Persist the uuid for the lifetime of this page-load. Pass `null` to clear.
 * Idempotent — re-setting the same value is a no-op.
 */
export function setPublicShareUuid(uuid: string | null): void {
  if (!uuid) {
    activeShareUuid = null;
    return;
  }
  const trimmed = uuid.trim();
  activeShareUuid = trimmed.length > 0 ? trimmed : null;
}

/** Are we currently in share-link recipient mode? */
export function isShareRecipientMode(): boolean {
  return activeShareUuid != null;
}

// ---------------------------------------------------------------------------
// Token counters
//
// Mirror of `TransformationPickerExternalUser.{RemainingTokens, MaxTokens}`.
// Populated by `SuperPickerPublicApp` after the share resolve succeeds and
// read by `PublicHeader` so every page (Site Plans, Project Details, Project
// Research) shows the same chip in the same spot.
//
// A tiny pub/sub keeps the React side simple: components subscribe via
// `useShareTokens()` and re-render when `setShareTokens()` is called (future
// rate-limit responses can decrement `remainingTokens` without prop drilling).
// ---------------------------------------------------------------------------

export interface ShareTokens {
  remaining: number;
  max: number;
}

let activeTokens: ShareTokens | null = null;
const tokenListeners = new Set<() => void>();

export function getShareTokens(): ShareTokens | null {
  return activeTokens;
}

export function setShareTokens(next: ShareTokens | null): void {
  if (next == null) {
    if (activeTokens == null) return;
    activeTokens = null;
  } else {
    const remaining = Number.isFinite(next.remaining) ? Math.max(0, Math.trunc(next.remaining)) : 0;
    const max = Number.isFinite(next.max) ? Math.max(0, Math.trunc(next.max)) : 0;
    if (activeTokens && activeTokens.remaining === remaining && activeTokens.max === max) {
      return;
    }
    activeTokens = { remaining, max };
  }
  tokenListeners.forEach((fn) => {
    try { fn(); } catch { /* swallow listener errors */ }
  });
}

export function subscribeShareTokens(listener: () => void): () => void {
  tokenListeners.add(listener);
  return () => { tokenListeners.delete(listener); };
}

// ---------------------------------------------------------------------------
// Trial expiry
//
// Mirror of the absolute `expiresAt` (ms-since-epoch) returned by the
// share resolve route. The SPA renders a live countdown from this and
// transitions itself to an "expired" state once the deadline passes,
// matching the backend's 410 (`SHARE_EXPIRED`) gate. Same pub/sub
// pattern as the token counters.
// ---------------------------------------------------------------------------

let activeExpiry: number | null = null;
const expiryListeners = new Set<() => void>();

export function getShareExpiry(): number | null {
  return activeExpiry;
}

export function setShareExpiry(next: number | null): void {
  if (next == null) {
    if (activeExpiry == null) return;
    activeExpiry = null;
  } else {
    if (activeExpiry === next) return;
    activeExpiry = next;
  }
  expiryListeners.forEach((fn) => {
    try { fn(); } catch { /* swallow listener errors */ }
  });
}

export function subscribeShareExpiry(listener: () => void): () => void {
  expiryListeners.add(listener);
  return () => { expiryListeners.delete(listener); };
}

// ---------------------------------------------------------------------------
// Inviting agent
//
// The agent who issued the share link (joined from `InvitedBy` to `User` on
// the backend). Surfaced so `PublicHeader` can render an "agent profile"
// button on every public page. Same pub/sub pattern as tokens.
// ---------------------------------------------------------------------------

export interface ShareInvitingAgent {
  ceaNumber: string;
  name: string | null;
  email: string | null;
  mobile: string | null;
  /** Absolute URL — already prefixed by `buildAgentPhotoUrl`. */
  photo: string | null;
}

let activeAgent: ShareInvitingAgent | null = null;
const agentListeners = new Set<() => void>();

export function getInvitingAgent(): ShareInvitingAgent | null {
  return activeAgent;
}

export function setInvitingAgent(next: ShareInvitingAgent | null): void {
  if (next == null) {
    if (activeAgent == null) return;
    activeAgent = null;
  } else {
    activeAgent = { ...next };
  }
  agentListeners.forEach((fn) => {
    try { fn(); } catch { /* swallow listener errors */ }
  });
}

export function subscribeInvitingAgent(listener: () => void): () => void {
  agentListeners.add(listener);
  return () => { agentListeners.delete(listener); };
}

// ---------------------------------------------------------------------------
// Recipient name
//
// The display name the inviting agent typed for this share recipient (the
// `Name` column on `TransformationPickerExternalUser`). Surfaced so the
// exported PDF cover can render a "Specially Prepared for <name>" block,
// mirroring the agent-side `clientName` slot used by the Purchase Planner
// PDF. No pub/sub: callers read it on demand at export time.
// ---------------------------------------------------------------------------

let activeRecipientName: string | null = null;

export function getRecipientName(): string | null {
  return activeRecipientName;
}

export function setRecipientName(next: string | null | undefined): void {
  const trimmed = typeof next === 'string' ? next.trim() : '';
  activeRecipientName = trimmed.length > 0 ? trimmed : null;
}

/**
 * Append `?uuid=<uuid>` (or merge into an existing query string) onto a URL
 * when running as a share-link recipient. Returns the original URL untouched
 * in agent mode.
 */
export function withShareUuid(input: string): string {
  if (!activeShareUuid) return input;
  // Preserve any existing query string.
  const separator = input.includes('?') ? '&' : '?';
  return `${input}${separator}uuid=${encodeURIComponent(activeShareUuid)}`;
}

// ---------------------------------------------------------------------------
// Share session JWT
//
// Issued by the backend after a successful OTP verify (see
// `signShareSessionToken` in `routes/superPickerShare.ts`) and required by
// every gated public endpoint. Stored in `localStorage` keyed by uuid so a
// page refresh on the SAME browser keeps working, but forwarding the URL
// to a different device leaves them without a token (the backend's
// `send`/`verify` routes both 409 once the share is activated, so the
// second device can't laundering a fresh token either).
//
// We deliberately use `localStorage` (per-origin, persistent) rather than
// `sessionStorage` (per-tab) because legitimate recipients commonly close
// and re-open the link from their own device — `localStorage` keeps that
// flow working. Forwarding the URL alone copies neither store.
// ---------------------------------------------------------------------------

const SESSION_STORAGE_PREFIX = 'super_picker_share_session:';

let activeSessionToken: string | null = null;

function readStoredSessionToken(uuid: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(SESSION_STORAGE_PREFIX + uuid);
    return raw && raw.trim().length > 0 ? raw : null;
  } catch {
    return null;
  }
}

function writeStoredSessionToken(uuid: string, token: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (token && token.trim().length > 0) {
      window.localStorage.setItem(SESSION_STORAGE_PREFIX + uuid, token);
    } else {
      window.localStorage.removeItem(SESSION_STORAGE_PREFIX + uuid);
    }
  } catch {
    /* swallow — private-mode browsers throw on localStorage writes */
  }
}

/** Returns the in-memory session token for the active share, or null. */
export function getShareSessionToken(): string | null {
  return activeSessionToken;
}

/**
 * Hydrate the in-memory session token from `localStorage` for a uuid.
 * Called on app boot once the uuid is known so a refreshed tab doesn't
 * lose access. Returns the token (or null when none is stored).
 */
export function loadShareSessionToken(uuid: string): string | null {
  const stored = readStoredSessionToken(uuid);
  activeSessionToken = stored;
  return stored;
}

/**
 * Persist the session token issued by `verifyShareOtp`. Pass `null` (or
 * omit the token) to clear both the in-memory cache and the stored entry
 * for this uuid (e.g. on share expiry).
 */
export function setShareSessionToken(
  uuid: string,
  token: string | null,
): void {
  activeSessionToken = token && token.trim().length > 0 ? token : null;
  writeStoredSessionToken(uuid, activeSessionToken);
}
